import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  UserCheck,
  Shield,
  Building2,
  Lock,
  CheckCircle2,
  AlertCircle,
  KeyRound,
  Mail,
  Phone,
  User
} from 'lucide-react';

export const ProfilePage: React.FC = () => {
  const { user, fetchWithAuth } = useAuth();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [pwdMessage, setPwdMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const getRoleLabel = () => {
    if (user?.role === 'deo' || user?.role === 'admin') return 'District Education Officer (DEO)';
    if (user?.role === 'beo') return `Block Education Officer (${user?.block || 'Block Admin'})`;
    if (user?.role === 'school' || user?.role === 'principal') return 'School Head / Principal';
    return 'Authorized Officer';
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwdMessage(null);

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPwdMessage({ type: 'error', text: 'All password fields are required.' });
      return;
    }

    if (newPassword !== confirmPassword) {
      setPwdMessage({ type: 'error', text: 'New password and confirmation do not match.' });
      return;
    }

    if (newPassword.length < 6) {
      setPwdMessage({ type: 'error', text: 'New password must be at least 6 characters.' });
      return;
    }

    setSavingPassword(true);
    try {
      const res = await fetchWithAuth('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentPassword, newPassword })
      }).then(r => r.json());

      if (res.success) {
        setPwdMessage({ type: 'success', text: 'Password successfully updated.' });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setPwdMessage({ type: 'error', text: res.message || 'Failed to update password.' });
      }
    } catch (err: any) {
      setPwdMessage({ type: 'error', text: err.message || 'Network error updating password.' });
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Page Header */}
      <div className="bg-white p-5 rounded-lg border border-slate-200 shadow-xs flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2.5 bg-blue-50 border border-blue-200 rounded text-blue-900">
            <UserCheck className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-base font-bold text-slate-900">Official Profile & Credentials</h1>
            <p className="text-xs text-slate-500">Government of Haryana — District Education Office, Nuh</p>
          </div>
        </div>
        <span className="px-3 py-1 bg-amber-100 text-amber-900 border border-amber-300 rounded text-xs font-bold uppercase tracking-wider">
          Active Official
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Officer Information Card */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-xs overflow-hidden">
          <div className="p-4 bg-[#0B1E38] text-white flex items-center space-x-2">
            <Shield className="w-4 h-4 text-amber-400" />
            <h2 className="text-xs font-bold uppercase tracking-wider">Officer Particulars</h2>
          </div>

          <div className="p-5 space-y-4 text-xs">
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Official Designation</label>
              <div className="text-sm font-bold text-slate-900 mt-0.5">{getRoleLabel()}</div>
            </div>

            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Portal Username</label>
              <div className="font-mono text-sm font-bold text-blue-900 mt-0.5 bg-slate-50 p-2 rounded border border-slate-200">
                {user?.username || 'user'}
              </div>
            </div>

            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Jurisdiction / Scope</label>
              <div className="text-xs font-semibold text-slate-800 mt-0.5">
                {user?.role === 'deo' || user?.role === 'admin'
                  ? 'District Nuh (All 5 Blocks & 902 Schools)'
                  : user?.role === 'beo'
                  ? `Block ${user?.block || 'Nuh'} (All Block Schools)`
                  : 'Assigned Government School (School ID: ' + (user?.school_id || 1) + ')'}
              </div>
            </div>

            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Department & Division</label>
              <div className="text-xs text-slate-700 mt-0.5">
                Department of School Education, Haryana • District Education Office, Nuh (Mewat)
              </div>
            </div>
          </div>
        </div>

        {/* Security & Password Card */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-xs overflow-hidden">
          <div className="p-4 bg-[#0B1E38] text-white flex items-center space-x-2">
            <KeyRound className="w-4 h-4 text-amber-400" />
            <h2 className="text-xs font-bold uppercase tracking-wider">Update Access Password</h2>
          </div>

          <form onSubmit={handlePasswordChange} className="p-5 space-y-3.5 text-xs">
            {pwdMessage && (
              <div className={`p-3 rounded text-xs font-bold flex items-center space-x-2 ${
                pwdMessage.type === 'success' ? 'bg-emerald-50 text-emerald-900 border border-emerald-300' : 'bg-rose-50 text-rose-900 border border-rose-300'
              }`}>
                {pwdMessage.type === 'success' ? <CheckCircle2 className="w-4 h-4 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
                <span>{pwdMessage.text}</span>
              </div>
            )}

            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">
                Current Password
              </label>
              <input
                type="password"
                required
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                className="w-full text-xs bg-slate-50 border border-slate-300 rounded px-3 py-2 text-slate-900 focus:outline-blue-800"
              />
            </div>

            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">
                New Password
              </label>
              <input
                type="password"
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 chars)"
                className="w-full text-xs bg-slate-50 border border-slate-300 rounded px-3 py-2 text-slate-900 focus:outline-blue-800"
              />
            </div>

            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">
                Confirm New Password
              </label>
              <input
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter new password"
                className="w-full text-xs bg-slate-50 border border-slate-300 rounded px-3 py-2 text-slate-900 focus:outline-blue-800"
              />
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={savingPassword}
                className="w-full py-2 bg-[#0B1E38] hover:bg-[#15345e] text-white text-xs font-bold rounded transition disabled:opacity-50"
              >
                {savingPassword ? 'Updating Password...' : 'Change Password'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
